// src/components/LegalPage.jsx
// Public Terms and Conditions / Privacy Policy pages (/terms, /privacy).
// Renders public/legal/{doc}.md with a small formatter for the subset of
// Markdown those documents use: headings, paragraphs, bullet lists, bold,
// italics, links and horizontal rules. Builds React elements, never HTML.

import React, { useEffect, useState } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';

const TITLES = { terms: 'Terms and Conditions', privacy: 'Privacy Policy' };

// **bold**, *italic*, [text](url)
function renderInline(text, keyPrefix) {
  const parts = [];
  const pattern = /(\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${i++}`;
    if (match[2] !== undefined) {
      parts.push(<strong key={key}>{renderInline(match[2], key)}</strong>);
    } else if (match[3] !== undefined) {
      parts.push(<em key={key}>{match[3]}</em>);
    } else {
      const href = match[5];
      const safe = /^(https?:|mailto:|\/)/.test(href) ? href : '#';
      parts.push(<a key={key} href={safe} style={{ color: '#c33329' }}>{match[4]}</a>);
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Bare web addresses and email addresses become links
function linkify(text) {
  return text
    .replace(/(^|[\s(])(https?:\/\/[^\s)]*[^\s).,;])/g, '$1[$2]($2)')
    .replace(/(^|[\s(])([\w.+-]+@[\w-]+\.[\w.-]*\w)/g, '$1[$2](mailto:$2)');
}

function renderMarkdown(md) {
  const blocks = [];
  let list = null;
  const flushList = () => {
    if (list) {
      const items = list;
      blocks.push(
        <ul key={`ul-${blocks.length}`} style={{ paddingLeft: '22px', margin: '0 0 14px 0' }}>
          {items.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{renderInline(linkify(item), `li-${blocks.length}-${i}`)}</li>)}
        </ul>
      );
      list = null;
    }
  };

  md.split('\n').forEach((raw, index) => {
    const line = raw.trimEnd();
    const key = `b-${index}`;
    if (line.startsWith('- ')) {
      (list = list || []).push(line.slice(2));
      return;
    }
    flushList();
    if (!line.trim()) return;
    if (line.startsWith('### ')) {
      blocks.push(<h3 key={key} style={{ fontSize: '17px', margin: '20px 0 8px', color: '#2d3748' }}>{renderInline(line.slice(4), key)}</h3>);
    } else if (line.startsWith('## ')) {
      blocks.push(<h2 key={key} style={{ fontSize: '20px', margin: '28px 0 10px', color: '#2d3748' }}>{renderInline(line.slice(3), key)}</h2>);
    } else if (line.startsWith('# ')) {
      // The page header already shows the title
    } else if (line === '---') {
      blocks.push(<hr key={key} style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '24px 0' }} />);
    } else {
      blocks.push(<p key={key} style={{ margin: '0 0 14px 0' }}>{renderInline(linkify(line), key)}</p>);
    }
  });
  flushList();
  return blocks;
}

function LegalPage({ doc }) {
  const [markdown, setMarkdown] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = `${TITLES[doc]} - Homework Solutions`;
    fetch(`/legal/${doc}.md`, { cache: 'no-cache' })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(setMarkdown)
      .catch(err => {
        console.error('Legal document load error:', err);
        setError(true);
      });
  }, [doc]);

  return (
    <div style={{ minHeight: '100vh', background: '#f7fafc', padding: '24px 16px' }}>
      <div style={{
        maxWidth: '820px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        padding: '32px clamp(18px, 5vw, 48px)',
        color: '#4a5568',
        fontSize: '15px',
        lineHeight: 1.6
      }}>
        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#c33329', fontWeight: 600, fontSize: '14px', textDecoration: 'none' }}>
          <ArrowLeft size={16} /> Back to Homework Solutions
        </a>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '28px', color: '#2d3748', margin: '20px 0 8px' }}>
          <FileText size={26} color="#c33329" /> {TITLES[doc]}
        </h1>
        <div style={{ marginBottom: '20px', fontSize: '14px' }}>
          <a href={doc === 'terms' ? '/privacy' : '/terms'} style={{ color: '#c33329' }}>
            {doc === 'terms' ? 'Read our Privacy Policy' : 'Read our Terms and Conditions'}
          </a>
        </div>
        {error && <p>Sorry, this page could not be loaded. Please try again later.</p>}
        {!error && markdown === null && <p>Loading...</p>}
        {markdown !== null && renderMarkdown(markdown)}
      </div>
    </div>
  );
}

export default LegalPage;
